import { AbstractChatClient } from 'shared/clients/abstractChatClient';
import smooch from 'smooch';
import { type MessagingChatAuth } from 'types/customEventData';

async function withCatch<TReturn>(
  promise: Promise<TReturn>,
  action: string,
): Promise<TReturn> {
  return promise?.catch((error) => {
    const errorMessage = error.message || JSON.stringify(error);

    throw new Error(`${action} failed: ${errorMessage}`);
  });
}

export class SmoochChatClient extends AbstractChatClient {
  private readonly integrationId: string;

  private isInitialized: boolean;

  constructor(integrationId: string) {
    super();
    this.isInitialized = false;
    this.integrationId = integrationId;
  }

  async init(options: Partial<InitOptions>): Promise<void> {
    if (this.isInitialized) return;

    await withCatch(
      smooch.init({
        ...options,
        integrationId: this.integrationId,
      }),
      'Init',
    );

    this.isInitialized = true;
  }

  async addTags(tags?: string[] | null): Promise<void> {
    if (!tags?.length) return;

    const tagsString = tags.join(',');

    const options = {
      metadata: {
        'zen:ticket:tags': tagsString,
      },
    };

    const conversation = smooch.getDisplayedConversation();

    if (!conversation) {
      await this.createConversation(options);

      return;
    }

    await this.updateConversation(conversation.id, options);
  }

  async updateConversation(
    conversationId: string,
    options: Partial<Conversation>,
  ): Promise<void> {
    await withCatch(
      smooch.updateConversation(conversationId, options),
      'Update conversation',
    );
  }

  async createConversation(options: Partial<Conversation>): Promise<void> {
    await withCatch(
      smooch.createConversation(options).then(() => undefined),
      'Create conversation',
    );
  }

  async identify({ token, externalId }: MessagingChatAuth): Promise<void> {
    if (!externalId) return;

    await withCatch(smooch.login(externalId, token), 'Identify');
  }

  async hideWidget(): Promise<void> {
    await withCatch(smooch.destroy(), 'Hide widget');

    this.isInitialized = false;
  }

  async logout(): Promise<void> {
    await withCatch(smooch.logout(), 'Logout');
  }

  openWidget(): void {
    smooch.open();
  }

  onReceivedMessage(callback: (message: Message) => void): void {
    smooch.on('message:received', callback);
  }

  onSentMessage(callback: (message: Message) => void): void {
    smooch.on('message:sent', callback);
  }
}
